import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  type UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Flex, Modal, Space, Tag, theme, Typography } from 'antd';
import _ from 'lodash';
import { CircleAlert, CircleCheck, PlusCircle, Trash2, Save } from 'lucide-react';
import PropTypes from 'prop-types';
import React, { type ReactNode, useEffect, useState } from 'react';
import { catchError, EMPTY, from, of, switchMap, tap, throwError } from 'rxjs';
import MealCategoryForm from './meal-category.form';
import AuthorizedComponent from '../../../components/authorized-component';
import { DataTable, type DataTableProps } from '../../../components/data-table';
import { OverflowComponent } from '../../../components/overflow.component';
import SyncSuspense from '../../../components/sync-suspense';
import { ExtraContent } from '../../../contexts/extra-content-context-provider';
import { type Beverage, MealCategory, type Supply } from '../../../entities';
import { useNotification } from '../../../hooks/use-notification';
import { AtomicOperation, useAtomicOperations, useQuery } from '../../../jsonapi-provider';
import { Roles } from '../../../types';
export interface MealCategoryFiltersProps {
  onChange?: (filters: FiltersState) => void;
  initialFilters: FiltersState;
}

interface FiltersState {
  query: string;
  showInActive: boolean;
}

const weekDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const mapStringToWeekDays = (availableDays: string): string[] => {
  const listOfDays: string[] = [];
  for (let i = 0; i < availableDays.length; i++) {
    availableDays[i] === '1' && listOfDays.push(weekDays[i]);
  }
  return listOfDays;
};
const SUCCESS_DELETE_MESSAGE = 'Meal category deleted successfully';

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  'data-row-key': string;
}
const Row: React.FC<Readonly<RowProps>> = (props) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props['data-row-key'],
  });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    cursor: 'move',
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  return <tr {...props} ref={setNodeRef} style={style} {...attributes} {...listeners} />;
};
const MealCategoriesPage = (): ReactNode => {
  const { token } = theme.useToken();
  const notification = useNotification();
  const [editedMealCategory, setEditedMealCategory] = useState<MealCategory>();
  const [dataSource, setDataSource] = useState<Array<MealCategory & { key: string | undefined }>>(
    []
  );
  const atomicOperations = useAtomicOperations();
  const mealCategories = useQuery<MealCategory[]>([
    'meal-category',
    {
      include: ['supplies', 'beverages'],
      page: {
        size: 9999,
        number: 1,
      },
      sort: ['sortOrder'],
    },
  ]);
  useEffect(() => {
    if (mealCategories.data) {
      setDataSource(
        mealCategories.data.map((item) => ({
          ...item,
          key: item.id,
        }))
      );
    }
  }, [mealCategories.data]);
  const supplies = useQuery<Supply[]>([
    'supply',
    {
      sort: 'name',
      page: {
        size: 10000,
        number: 1,
      },
    },
  ]);
  const beverages = useQuery<Beverage[]>([
    'beverage',
    {
      page: {
        size: 10000,
        number: 1,
      },
    },
  ]);
  useEffect(() => {
    mealCategories.error &&
      notification.error({
        message: ' ',
        description: mealCategories.error.detail,
      });
  }, [mealCategories.error]);

  const columns: DataTableProps<MealCategory>['columns'] = [
    {
      title: 'Name',
      dataIndex: ['name'],
      render: (value, item) => (
        <Typography.Link
          onClick={() => {
            setEditedMealCategory(item);
          }}
        >
          {value}
        </Typography.Link>
      ),
    },
    {
      title: 'Description',
      dataIndex: ['description'],
    },
    {
      title: 'Min Allowed Quantity',
      dataIndex: ['minQuantityAllowed'],
    },
    {
      title: 'Available Week Days',
      dataIndex: 'availableWeekDays',
      render: (availableWeekDays) =>
        availableWeekDays === '1111111' ? (
          <Tag color={token.colorPrimary}>All Week</Tag>
        ) : (
          <>
            {mapStringToWeekDays(availableWeekDays).map((day, index) => {
              return (
                <Tag key={index} color="processing">
                  {day}
                </Tag>
              );
            })}
          </>
        ),
    },
    {
      title: 'Has Diabetic Type',
      dataIndex: 'hasDiabeticType',
      render: (hasDiabeticType) =>
        hasDiabeticType ? (
          <Flex align="center" gap="small">
            <CircleCheck style={{ color: token.colorTextDisabled }} />
            <Typography.Text>Allowed</Typography.Text>
          </Flex>
        ) : (
          <span></span>
        ),
    },

    {
      title: 'Diets',
      dataIndex: 'diets',
      render: (diets) =>
        diets?.length ? (
          <OverflowComponent
            elementsList={diets.split(',').map((diet: string, index: number) => (
              <Tag key={index}>{diet}</Tag>
            ))}
          />
        ) : (
          ''
        ),
    },
    {
      title: 'Preferences',
      dataIndex: 'preferences',
      render: (preferences) =>
        preferences?.length ? (
          <OverflowComponent
            elementsList={preferences.split(',').map((preference: string, index: number) => (
              <Tag key={index}>{preference}</Tag>
            ))}
          />
        ) : (
          ''
        ),
    },
    {
      title: 'Supplies',
      dataIndex: 'supplies',
      render: (supplies) =>
        supplies?.length ? (
          <OverflowComponent
            elementsList={supplies.map((supply: Supply, index: number) => (
              <Tag key={index}>{supply.name}</Tag>
            ))}
          />
        ) : (
          ''
        ),
    },
    {
      dataIndex: 'sortOrder',
      hidden: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      sorter: true,
      defaultSortOrder: 'ascend',
    },
  ];
  const orderingChanged = (): boolean => {
    return dataSource.some((element, idx: number) => {
      return element.id !== mealCategories.data?.[idx].id;
    });
  };

  const saveOrdering = (): void => {
    const mealCategoriesOperations = dataSource.map((mealCategory: MealCategory, idx: number) => {
      return AtomicOperation.update(
        _.create(
          MealCategory.prototype,
          _.pick(Object.assign(new MealCategory(), mealCategory, { sortOrder: idx + 1 }), [
            'id',
            'sortOrder',
          ])
        )
      );
    });
    from(atomicOperations.operate(mealCategoriesOperations))
      .pipe(
        tap((result) => {
          if (result.some((r) => r.error)) {
            throwError(
              () => new Error(result.find((r) => r.error)?.error?.detail ?? 'Unknown error')
            );
          }
          notification.success({
            message: ' ',
            description: 'Meal categories ordering saved successfully',
          });
          mealCategories.refetch?.();
        }),
        catchError((error: Error) => {
          notification.error({
            message: ' ',
            description: error.message,
          });
          return EMPTY;
        })
      )
      .subscribe();
  };
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    })
  );
  const onDragEnd = ({ active, over }: DragEndEvent): void => {
    if (active.id !== over?.id) {
      setDataSource((prev) => {
        const activeIndex = prev.findIndex((i) => i.key === active.id);
        const overIndex = prev.findIndex((i) => i.key === over?.id);
        return arrayMove(prev, activeIndex, overIndex);
      });
    }
  };
  return (
    <Flex gap="middle" vertical>
      <DndContext sensors={sensors} modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
        <SortableContext
          items={dataSource.map((i) => i.key) as UniqueIdentifier[]}
          strategy={verticalListSortingStrategy}
        >
          <DataTable<MealCategory>
            title="Meal categories"
            description="List of meal categories meals can be categorized by"
            components={{
              body: { row: Row },
            }}
            rowKey="key"
            actionsNode={
              <AuthorizedComponent roles={[Roles.ROLE_HHC_ADMIN]}>
                <Space direction="horizontal" size="small">
                  <Button
                    type="primary"
                    icon={<PlusCircle />}
                    onClick={() => {
                      setEditedMealCategory(new MealCategory());
                    }}
                  >
                    Add Meal Category
                  </Button>
                  {orderingChanged() && (
                    <Button
                      type="primary"
                      icon={<Save />}
                      onClick={() => {
                        saveOrdering();
                      }}
                      loading={
                        atomicOperations.isLoading ||
                        mealCategories.isLoading ||
                        mealCategories.isFetching
                      }
                    >
                      Save ordering
                    </Button>
                  )}
                </Space>
              </AuthorizedComponent>
            }
            loading={mealCategories.isLoading || mealCategories.isFetching}
            dataList={dataSource}
            onChange={() => {}}
            columns={columns}
            pagination={false}
            actions={[
              {
                key: 'delete',
                label: 'Delete',
                icon: <Trash2 />,
                danger: true,
                onItemClick: (record) => {
                  Modal.confirm({
                    width: '50%',
                    title: 'Confirm deletion',
                    icon: <CircleAlert />,
                    content: (
                      <Typography.Text>
                        Are you sure you want to delete <b>{record.name}</b> permanently?
                      </Typography.Text>
                    ),
                    okText: 'Yes',
                    okType: 'danger',
                    cancelText: 'No',
                    onOk: () => {
                      from(mealCategories.client.delete(['meal-category', record.id!]))
                        .pipe(
                          switchMap((result) =>
                            result.error
                              ? throwError(() => new Error(result.error?.detail))
                              : of(result)
                          ),
                          tap(() => {
                            notification.success({
                              message: ' ',
                              description: SUCCESS_DELETE_MESSAGE,
                            });
                            mealCategories.refetch?.();
                          }),
                          catchError((error: Error) => {
                            notification.error({
                              message: ' ',
                              description: error.message,
                            });
                            return EMPTY;
                          })
                        )
                        .subscribe();
                    },
                  });
                },
              },
            ]}
          />
        </SortableContext>
      </DndContext>
      <SyncSuspense fallback={''} condition={editedMealCategory}>
        <ExtraContent>
          <MealCategoryForm
            mealCategory={editedMealCategory ?? new MealCategory()}
            onSuccess={() => {
              mealCategories.refetch?.();
              setEditedMealCategory(undefined);
            }}
            supplies={supplies}
            beverages={beverages}
            onCancel={() => {
              setEditedMealCategory(undefined);
            }}
          />
        </ExtraContent>
      </SyncSuspense>
    </Flex>
  );
};

MealCategoriesPage.propTypes = {
  defaultPageNumber: PropTypes.number,
  defaultPageSize: PropTypes.number,
};

MealCategoriesPage.defaultProps = {
  defaultPageNumber: 1,
  defaultPageSize: 10,
  onVisibleColumnsChange: () => {},
  onSelectRowsChange: () => {},
};

MealCategoriesPage.displayName = 'MealCategoriesPage';
export default MealCategoriesPage;
